import { defineStore } from 'pinia';
import { reactive } from 'vue';
import { IBulkEditBusinessState } from './types';
import { initBulkEditBusinessState } from './initValue';
import { diContainer } from '@/coreContainer/container';
import { BulkEditBusinessAdapter } from '@/editBusinessModule/adapters/BulkEditBusinessAdapter';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { FieldState } from '@/corePagesModule/composables/useForm';

export const useBulkEditBusinessStore = defineStore('bulkEditBusiness', () => {
  const state = reactive<IBulkEditBusinessState>(initBulkEditBusinessState());

  /**
   * Stateをリセットする
   */
  const $reset = () => {
    Object.assign(state, initBulkEditBusinessState());
  };

  /**
   * 初期化処理
   * @param beforeFetchProcess - フェッチ前の処理
   */
  const init = async (beforeFetchProcess?: (...args: unknown[]) => unknown | Promise<unknown>) => {
    $reset();
    if (beforeFetchProcess) await beforeFetchProcess();
    await fetchEditBusiness();
  };

  /**
   * 取引一覧を取得する
   */
  const fetchEditBusiness = async () => {
    state.loadingStatus.type = 'loading';
    state.checkedIds = new Set();
    let result: INetworkResult<{ businesses: IBusiness[]; pagingOptions: IPagingOptions }, IError>;
    if (state.businessMode === 'baseBusiness') {
      result = await diContainer.bulkGetBaseBusinessUseCase.execute(
        state.sortInfo.type,
        state.sortInfo.key,
        state.pagingOptions.pageSize,
        state.pagingOptions.offset,
        state.filterItems,
        BulkEditBusinessAdapter.adaptBulkBaseBusiness
      );
    } else {
      result = await diContainer.bulkGetAggregateBusinessUseCase.execute(
        state.sortInfo.type,
        state.pagingOptions.pageSize,
        state.pagingOptions.offset,
        state.sortInfo.key,
        state.filterItems,
        BulkEditBusinessAdapter.adaptBulkAggregateBusiness
      );
    }
    switch (result.status.type) {
      case 'success':
        state.pagingOptions = result.status.data.pagingOptions ?? initPagingOptions(30);
        state.dataList = result.status.data.businesses ?? [];
        state.loadingStatus.type = state.dataList.length > 0 ? 'success' : 'zero-match';
        return;
      case 'error':
      default:
        state.loadingStatus.type = 'error';
        return;
    }
  };

  /**
   * 追加でフォームのバリデーションを行い、fieldStateにエラー情報を追加する
   * @param fieldState - フォームの状態
   */
  const additionalValidate = (fieldState?: FieldState): string[] => {
    if (!fieldState) return [];
    const pairItems = [
      ['warehouseName', 'warehouseNumber'],
      ['supplierName', 'supplierNumber'],
      ['businessName', 'businessNumber'],
      ['customerName', 'customerNumber'],
      ['salesDestinationName', 'salesDestinationNumber'],
      ['leadTime', 'leadTimeUnit'],
      ['stdInventoryIndicesMin', 'stdInventoryIndicesMax', 'stdInventoryIndicesUnit'],
    ];

    const invalidItems: string[] = [];
    for (const pair of pairItems) {
      const truthy = pair.filter((v) => !!fieldState[v]?.value.value);
      const falsy = pair.filter((v) => !fieldState[v]?.value.value);

      const allSame = truthy.length === 0 || falsy.length === 0;
      if (!allSame) invalidItems.push(...falsy);
    }
    return invalidItems;
  };

  return {
    state,
    $reset,
    init,
    fetchEditBusiness,
    additionalValidate,
  };
});

// export const useEditMasterStore = defineStore('editMaster', () => {});
